export enum ERoutes {
    "dashboard" = "/dashboard",

    "onboarding" = "/onboarding",

    "accounts" = "/accounts",

    "assets" = "/assets",

    "systemEmail" = "/account/system-emails",
    "billing" = "/account/plans-and-billing",

    "accountProfile" = "/account",

    // USERS
    "users" = "/users",
    "createUser" = "/account/users/create",
    "userProfile" = "/account/users/:userId",
    "senders" = "/account/senders",

    "domainTracking" = "/account/tracking",
    "systemEmails" = "/account/systemEmails",

    "emailAPI" = "/email-api",
    "emailMetrics" = "/email-api/metrics",
    "emailDeliverability" = "/email-api/deliverability",
    "emailReports" = "/email-api/reports",
    "emailRequestLog" = "/email-api/requestLog",

    "apiGettingStarted" = "/email-api/doc/getting-started",
    "apiReferences" = "/email-api/doc/references",

    "export" = "/exports",
    "suppressionList" = "/suppression-list",
    "suppressionListAdd" = "/account/suppression-list/add",

    "contacts" = "/contacts",
    "createList" = "/lists/default/manage/new",
    "importContacts" = "/contacts/import/list",
    "addContacts" = "/contacts/import/add-contacts",
    "contactDetails" = "/contacts/:contactId/:listId",
    "lists" = "/lists",
    "listEdit" = "/lists/:listId/manage",

    "createSegment" = "/contacts/segments/create",

    "campaigns" = "/campaigns",
    "campaignDrafts" = "/campaigns/drafts",
    "campaignOutbox" = "/campaigns/outbox",
    "campaignSent" = "/campaigns/sent",
    "campaignReport" = "/campaigns/:campaignId/stats/performance",
    "campaignsCreate" = "/campaigns/create",
    "campaignEdit" = "/campaigns/:campaignId/edit",

    "campaignsABCreate" = "/campaigns/abtests/create",
    "campaignsABTest" = "/campaigns/abtests",
    "campaignABTestDrafts" = "/campaigns/abtests/drafts",
    "campaignABTestActive" = "/campaigns/abtests/active",
    "campaignABTestComplete" = "/campaigns/abtests/complete",
    "campaignABTestStats" = "/campaigns/abtests/:abtestId/stats",


    "templateEdit" = "/campaigns/templates/:templateId/edit",
    "templateBrowse" = "/campaigns/templates/browse",

    "templates" = "/templates",
    "templateDetails" = "/templates/:templateId",
    //"templateEdit" = "/templates/:templateId/edit",
    "templateSharing" = "/templates/:templateId/sharing",

    "automations" = "/automations",
    "createAutomation" = "/automations/create",
    "automationEdit" = "/automations/:automationId/edit",

    "forms" = "/forms",
    "legacyForms" = "/forms/basic",
    "createForm" = "/forms/new",
    "formDetails" = "/forms/:formId",
    "formEdit" = "/forms/:formId/edit",
    "formEditBasic" = "/forms/basic/:formId/linked-form",

    "popups" = "/popups",
    "createPopup" = "/popups/new",
    "popupDetails" = "/popups/:popupId",
    "popupEdit" = "/popups/:popupId/edit",

    "pages" = "/pages",
    "createPage" = "/pages/new",
    "pageDetails" = "/pages/:pageId",
    "pageEdit" = "/pages/:pageId/edit",

    "brand" = "/brand",

    "stripeIntegration" = "/stripe",

    "integrations" = "/integrations",

    "fileManager" = "/images",

    // Public routes
    "publicForm" = "/public/f/:token",
    "listSubscription" = "/public/list/subscribe",
    "listConfirmation" = "/public/list/confirm",
}


/**
 * Extracts path parameters from the current URL by matching it against defined routes
 * Similar to useLocation() functionality for micro frontend scenarios where host container does 
 * not provide the proper routing context
 * @param path - The path to extract params from (e.g., from window.location.hash or window.location.pathname)
 * @returns An object containing the matched route and extracted path parameters
 */
export function getCurrentPathParams(path?: string): {
    matchedRoute: string | null
    params: Record<string, string>
    routeKey: keyof typeof ERoutes | null
} {
    let currentPath = path || window.location.hash
    let params: Record<string, string> = {}
    let matchedRoute: string | null = null
    let routeKey: keyof typeof ERoutes | null = null

    // Convert current path to ensure it starts with /
    let normalizedPath = currentPath.startsWith('#') ? currentPath.substring(1) : currentPath
    normalizedPath = normalizedPath.startsWith('/') ? normalizedPath : `/${normalizedPath}`

    // Remove query parameters from the path
    normalizedPath = normalizedPath.split('?')[0]

    // Iterate through all routes to find a match
    for (const [key, route] of Object.entries(ERoutes)) {
        if (isRouteMatch(normalizedPath, route)) {
            matchedRoute = route
            routeKey = key as keyof typeof ERoutes
            params = extractParamsFromRoute(normalizedPath, route)
            break
        }
    }

    return {
        matchedRoute,
        params,
        routeKey
    }
}

/**
 * Checks if a given path matches a route pattern
 * @param path - The current pathname
 * @param route - The route pattern to match against
 * @returns True if the path matches the route pattern
 */
function isRouteMatch(path: string, route: string): boolean {
    // Convert route pattern to regex
    const routeRegex = route
        .replace(/:[^/]+/g, '[^/]+') // Replace :param with regex pattern
        .replace(/\//g, '\\/') // Escape forward slashes

    const fullRegex = new RegExp(`^${routeRegex}$`)
    return fullRegex.test(path)
}

/**
 * Extracts path parameters from a matched route
 * @param path - The current pathname
 * @param route - The matched route pattern
 * @returns Object containing the extracted parameters
 */
function extractParamsFromRoute(path: string, route: string): Record<string, string> {
    const params: Record<string, string> = {}

    const pathSegments = path.split('/')
    const routeSegments = route.split('/')

    if (pathSegments.length !== routeSegments.length) {
        return params
    }

    for (let i = 0; i < routeSegments.length; i++) {
        const routeSegment = routeSegments[i]
        const pathSegment = pathSegments[i]

        if (routeSegment.startsWith(':')) {
            // Extract parameter name (remove the colon)
            const paramName = routeSegment.slice(1)
            params[paramName] = pathSegment
        }
    }

    return params
}
